import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Stack, Typography } from '@mui/material';
import { ControllerRenderProps } from 'react-hook-form';
import {
  useCaptableActions,
  useMakeOtherPrimaryAndDelete,
  useMakeOtherPrimaryAndDeleteLegacy,
} from '../CapTableActions';
import { getCaptablePrimarySourceKey } from '../captable-utils';
import { selectedCompanyIdProfileAtom } from '../../state/UIStateJ';
import { ConfirmDeletionDialog } from '../../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { companyStateJ } from '../../../../services/state/CompanyStateJ';
import {
  captablesByCompanyStateJ,
  selectedCaptableStateJ,
} from '../../../../services/state/CapTableDataStateJ';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { FieldSelect } from '../../../../components/Form/Display/FieldSelect';
import { useCaptableSelectorField } from './CaptableSelector';
import { useAtomValue } from 'jotai';

interface IDeleteCaptableDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

export function DeleteCaptableDialog({ open, setOpen }: IDeleteCaptableDialogProps) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const currentCaptable = useAtomValue(selectedCaptableStateJ(companyId));
  const allCaptables = useAtomValue(captablesByCompanyStateJ(companyId));

  if (!currentCaptable || !open || !allCaptables || allCaptables.length === 0) {
    return null;
  }

  if (allCaptables.length === 1 || !currentCaptable.primary) {
    return <ConfirmDelete open={open} setOpen={setOpen} />;
  }
  return <ConfirmDeleteAndSetNewPrimary open={open} setOpen={setOpen} />;
}

export function ConfirmDelete({ open, setOpen }: IDeleteCaptableDialogProps) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const company = useAtomValue(companyStateJ(companyId));
  const currentCaptable = useAtomValue(selectedCaptableStateJ(companyId));

  const { deleteCaptableHandler } = useCaptableActions();

  const [loading, setLoading] = useState(false);

  const onConfirmDelete = useCallback(async () => {
    if (!currentCaptable) {
      return;
    }
    setLoading(true);
    await deleteCaptableHandler(currentCaptable);
    setLoading(false);
    setOpen(false);
  }, [currentCaptable, deleteCaptableHandler, setOpen]);

  return (
    <ConfirmDeletionDialog
      open={open}
      onClose={() => setOpen(false)}
      onConfirm={onConfirmDelete}
      title={'Are you sure you want to delete this cap table?'}
      loading={loading}
    >
      <Typography variant='body2' color='text.secondary'>
        All related data will be deleted permanently.
      </Typography>
      {currentCaptable?.primary && (
        <Typography variant='body2' color='text.secondary'>
          {`Deleting this primary cap table will leave company ${company?.name} with no cap table.`}
        </Typography>
      )}
    </ConfirmDeletionDialog>
  );
}

export function ConfirmDeleteAndSetNewPrimary({ open, setOpen }: IDeleteCaptableDialogProps) {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const currentCaptable = useAtomValue(selectedCaptableStateJ(companyId));
  const allCaptables = useAtomValue(captablesByCompanyStateJ(companyId));
  const captableOptions = useMemo(
    () => (allCaptables ?? []).filter((c) => c.id !== currentCaptable?.id),
    [allCaptables, currentCaptable]
  );
  const [loading, setLoading] = useState(false);
  const [newPrimaryId, setNewPrimaryId] = useState<number>(() => captableOptions[0]?.id);

  const captableField = useCaptableSelectorField();
  const field = useMemo(() => {
    return {
      ...captableField,
      rendererMeta: {
        ...captableField.rendererMeta,
        values: captableField.rendererMeta?.values?.filter((v) => v.value !== currentCaptable?.id) ?? [],
      },
    };
  }, [captableField, currentCaptable?.id]);

  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();
  const makeOtherPrimaryAndDelete = useMakeOtherPrimaryAndDelete();
  const makeOtherPrimaryAndDeleteLegacy = useMakeOtherPrimaryAndDeleteLegacy();

  const onMakeOtherPrimaryAndDelete = useCallback(async () => {
    if (!currentCaptable || !newPrimaryId) {
      return;
    }
    setLoading(true);
    if (asOfCapTables) {
      const newPrimaryCaptable = (allCaptables ?? []).find((c) => c.id === newPrimaryId);
      if (!newPrimaryCaptable) {
        setLoading(false);
        return;
      }
      await makeOtherPrimaryAndDelete(
        companyId,
        getCaptablePrimarySourceKey(newPrimaryCaptable),
        currentCaptable.id
      );
    } else {
      await makeOtherPrimaryAndDeleteLegacy(newPrimaryId, currentCaptable.id);
    }
    setLoading(false);
    setOpen(false);
  }, [
    currentCaptable,
    allCaptables,
    makeOtherPrimaryAndDelete,
    makeOtherPrimaryAndDeleteLegacy,
    asOfCapTables,
    newPrimaryId,
    companyId,
    setOpen,
  ]);

  return (
    <ConfirmDeletionDialog
      open={open}
      onClose={() => setOpen(false)}
      onConfirm={onMakeOtherPrimaryAndDelete}
      title={'Are you sure you want to delete this cap table?'}
      loading={loading}
    >
      <Typography variant='body2' color='text.secondary' mt='1rem' component={'div'}>
        <Stack spacing={3}>
          <p>All related data will be deleted permanently.</p>
          <p>Please select new Primary cap table before proceeding</p>
        </Stack>
      </Typography>

      <FieldSelect
        formField={field}
        formProps={{ onChange: (val) => setNewPrimaryId(val), value: newPrimaryId } as ControllerRenderProps}
      />
    </ConfirmDeletionDialog>
  );
}
